import { useT } from '@/shared/i18n';
import { WorkerizeWordmark } from '@/shared/ui/brand';
import type { ReactNode } from 'react';

/**
 * SiteFooter — the public landing footer (extraction §2/§23c). Dark Ink band
 * (`--ink-band`, theme-stable), rounded top, brand recap + two link columns +
 * bottom bar. Logo = Brand System V1 lockup. `year` is passed in (Date.now is
 * unavailable in some contexts; the page supplies it).
 */
export function SiteFooter({ year }: { year: number }): ReactNode {
  const { t } = useT();
  const platform = [
    t('landing.footer.platform.1'),
    t('landing.footer.platform.2'),
    t('landing.footer.platform.3'),
  ];
  const company = [
    t('landing.footer.company.1'),
    t('landing.footer.company.2'),
    t('landing.footer.company.3'),
  ];
  return (
    <footer
      data-testid="site-footer"
      className="rounded-t-[40px] border-t border-[var(--ink-band-border)] bg-[var(--ink-band)] pb-12 pt-20 text-[var(--on-ink-band)]"
    >
      <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-desktop">
        <div className="mb-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <WorkerizeWordmark tone="onDark" size={32} className="mb-6" />
            <p className="max-w-md text-sm leading-relaxed text-[var(--on-ink-band-variant)]">
              {t('landing.footer.tagline')}
            </p>
          </div>
          <div>
            <h4 className="mb-6 text-xs font-semibold uppercase tracking-widest text-secondary-fixed">
              {t('landing.footer.col.platform')}
            </h4>
            <ul className="space-y-4">
              {platform.map((label) => (
                <li key={label}>
                  <a
                    href="#"
                    className="text-sm text-[var(--on-ink-band-variant)] transition-colors hover:text-secondary-fixed"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="mb-6 text-xs font-semibold uppercase tracking-widest text-secondary-fixed">
              {t('landing.footer.col.company')}
            </h4>
            <ul className="space-y-4">
              {company.map((label) => (
                <li key={label}>
                  <a
                    href="#"
                    className="text-sm text-[var(--on-ink-band-variant)] transition-colors hover:text-secondary-fixed"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex flex-col items-center justify-between gap-6 border-t border-[var(--ink-band-border)] pt-8 md:flex-row">
          <p className="text-xs text-[var(--on-ink-band-variant)]">
            © {year} {t('app.name')} · {t('landing.footer.rights')}
          </p>
          <p className="text-xs text-[var(--on-ink-band-variant)]">
            <span>English</span>
            <span aria-hidden="true"> · </span>
            <span dir="rtl" lang="ar">
              العربية
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
